import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import { analyticsService } from 'src/services/analytics/analyticsService';
import {
  RFQ_BUILDER_CHAT_COMPLETED,
  RFQ_DRAWER_CLOSED,
  RFQ_DRAWER_OPENED
} from 'src/services/analytics/rfqBuilderEvents';
import { useMediaQuery } from 'src/shared/hooks';
import { Rfq } from 'src/shared/types/rfq';

import { cn } from 'src/shared/components/ui';

import RfqDrawer from './rfq-drawer/RfqDrawer';
import RfqDrawerTrigger from './rfq-drawer/RfqDrawerTrigger';
import RfqBuilderChat from './RfqBuilderChat';

interface RfqPlaygroundProps {
  rfq?: Rfq;
  generateRfq: () => Promise<void>;
  generatedContent: string;
  isGenerating: boolean;
  /**
   * When true the drawer starts open — used when resuming from a saved
   * `builder_step = 'review'` without a full page transition.
   */
  initialDrawerOpen?: boolean;
}

/**
 * The main chat + document-preview playground. Manages the side-by-side (or
 * full-screen) drawer in-page, and reflects the open/closed state as the
 * `review` / `playground` builder step in both the URL and the backend.
 */
const RfqPlayground: React.FC<RfqPlaygroundProps> = ({
  rfq,
  generateRfq,
  generatedContent,
  isGenerating,
  initialDrawerOpen = false
}) => {
  const [, setSearchParams] = useSearchParams();
  const [isDrawerOpen, setIsDrawerOpen] = useState(initialDrawerOpen);
  const [isChatFinished, setIsChatFinished] = useState(false);
  const [isDrawerFullyOpen, setIsDrawerFullyOpen] = useState(false);
  const isMobile = useMediaQuery();

  // When resuming directly into review (initialDrawerOpen=true), kick off generation.
  useEffect(() => {
    if (initialDrawerOpen) {
      generateRfq();
    }
    // Intentionally runs only on mount; generateRfq closure handles the rfqId.
  }, []);

  useEffect(() => {
    if (isDrawerOpen) {
      const timer = setTimeout(() => {
        setIsDrawerFullyOpen(true);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setIsDrawerFullyOpen(false);
    }
  }, [isDrawerOpen]);

  const setStepParam = (step: 'playground' | 'review') => {
    if (!rfq?.rfq?.id) return;
    setSearchParams({ rfq: rfq.rfq.id.toString(), step });
  };

  const handleDrawerToggle = () => {
    const opening = !isDrawerOpen;

    if (opening) {
      analyticsService.trackEvent(RFQ_DRAWER_OPENED, {
        rfq_id: rfq?.rfq?.id,
        trigger: 'manual'
      });
      generateRfq();
      // Deliberately no step change — only the explicit "review RFQ" action moves to review.
    } else {
      analyticsService.trackEvent(RFQ_DRAWER_CLOSED, { rfq_id: rfq?.rfq?.id });
      setStepParam('playground');
    }
    setIsDrawerOpen(opening);
    setIsChatFinished(false);
  };

  /** Called when the user explicitly clicks "review RFQ" (eye icon). */
  const handleReviewOpen = () => {
    analyticsService.trackEvent(RFQ_DRAWER_OPENED, {
      rfq_id: rfq?.rfq?.id,
      trigger: 'review'
    });
    generateRfq();
    setIsDrawerOpen(true);
    setIsChatFinished(false);
    setStepParam('review');
  };

  const onFinishChat = () => {
    analyticsService.trackEvent(RFQ_BUILDER_CHAT_COMPLETED, {
      rfq_id: rfq?.rfq?.id
    });
    analyticsService.trackEvent(RFQ_DRAWER_OPENED, {
      rfq_id: rfq?.rfq?.id,
      trigger: 'chat_finished'
    });
    generateRfq();
    setIsChatFinished(true);
    setIsDrawerOpen(true);
    setStepParam('review');
  };

  const handleBack = () => {
    analyticsService.trackEvent(RFQ_DRAWER_CLOSED, { rfq_id: rfq?.rfq?.id });
    setIsChatFinished(false);
    setIsDrawerOpen(false);
    setStepParam('playground');
  };

  return (
    <motion.div
      data-testid="build-anything-display"
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        maxWidth: isChatFinished
          ? '100%'
          : isDrawerOpen
            ? '100%'
            : `${isMobile ? '100%' : '700px'}`
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="h-dvh flex flex-col bg-white mx-auto w-full"
    >
      {!isDrawerOpen && (
        <RfqDrawerTrigger onClick={handleDrawerToggle} isDrawerOpen={false} />
      )}

      <div className="flex h-dvh w-full relative">
        {/* Left Side - Chat Content */}
        <motion.div
          initial={false}
          animate={{
            left: isChatFinished || (isDrawerOpen && isMobile) ? '-100%' : '0',
            width: isChatFinished
              ? '0'
              : isDrawerOpen
                ? `${isMobile ? '100%' : '50%'}`
                : '100%',
            right: isChatFinished ? 'auto' : '0',
            opacity: isChatFinished || (isDrawerOpen && isMobile) ? 0 : 1
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="fixed top-0 bottom-0 right-0 left-0 flex flex-col z-10 bg-white overflow-y-auto cavela-scrollbar w-full h-dvh"
          style={{
            backgroundImage: isDrawerOpen
              ? 'none'
              : `
              linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            backgroundColor: '#FFF'
          }}
        >
          <motion.div
            initial={{ left: '50%', transform: 'translateX(-50%)' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className={cn(
              'absolute px-5 pr-4 flex flex-col w-full chat-bg transition-all duration-300 max-w-[800px] mobile:max-w-full flex-1 h-full min-h-0 max-h-svh',
              isDrawerOpen && isDrawerFullyOpen && 'max-w-full'
            )}
          >
            <RfqBuilderChat
              isDrawerOpen={isDrawerOpen}
              rfq={rfq}
              handleDrawerToggle={handleDrawerToggle}
              onReviewRfq={handleReviewOpen}
              isGeneratingRfq={isGenerating}
              onFinishChat={onFinishChat}
            />
          </motion.div>
        </motion.div>

        {/* Right Side - Drawer Content */}
        <AnimatePresence>
          {isDrawerOpen && (
            <motion.div
              initial={{ x: '100%', opacity: 0 }}
              animate={{
                x: 0,
                opacity: 1,
                width: isChatFinished ? '100%' : `${isMobile ? '100%' : '50%'}`
              }}
              exit={{ x: '100%', opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="fixed top-0 right-0 bottom-0 h-dvh flex flex-col overflow-hidden z-20"
            >
              <RfqDrawerTrigger
                onClick={handleDrawerToggle}
                isDrawerOpen={true}
              />
              <RfqDrawer
                rfq={rfq}
                currentGeneratedContent={generatedContent}
                generatedContent={generatedContent}
                isGenerating={isGenerating}
                isChatFinished={isChatFinished}
                isDrawerOpen={isDrawerOpen}
                onBack={handleBack}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default RfqPlayground;
